import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import { ICreatePlant } from "./interfaces/create-plants.interface";
import { IUpdatePlant } from "./interfaces/update-plants.interface";
import PlantsAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/configurations/plants/plants.entity";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { IFilters } from "../../filters/interfaces/filterInterface";


const apiUrl = env.API_CONFIGURATION_URL;
const moduleName = "plants";

@Injectable({
  providedIn: "root",
})
export class PlantsService extends AbstractService<
  PlantsAttributes,
  ICreatePlant,
  IUpdatePlant
> {

  /** The last filter used to load the list */
  currentFilter: CombinedFilter<PlantsAttributes>;

  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  findByFilter(filter: CombinedFilter<PlantsAttributes>, options?: RequestOptions) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
    return this.http.get<IPaginationResult<PlantsAttributes>>(API_URL, { headers });
  }

  /**
   * Obtiene plantas con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Dos objetos (OR lógico) - uno busca en 'name', otro en 'externalKeys.code'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param dataToFilter Filters to apply
   * @returns Paginated data
   *
   * Search fields: name, externalKeys.code
   * Array filters: statusId, companyId
   */
  findAllWithCount(
    options: IPagination<PlantsAttributes>,
    dataToFilter: IFilters,
  ): Observable<IPaginationResult<PlantsAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtro de compañía, filtro de estados.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<PlantsAttributes>[] => {
      const deletedFilter: FilterFn<PlantsAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        dataToFilter?.companyId
          ? andWhere('company.id' as keyof PlantsAttributes, Ops.eq(dataToFilter.companyId))
          : noop(),
        dataToFilter?.status && dataToFilter.status.length > 0
          ? andWhere("statusId", Ops.in(...dataToFilter.status))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por NAME (o solo condiciones si no hay búsqueda)
    const searchByNameQueryObject: FilterFn<PlantsAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("name", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por CODE (solo si hay búsqueda)
    const searchByCodeQueryObject: FilterFn<PlantsAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto, crea el OR)
      andWhere("externalKeys.code" as keyof PlantsAttributes, Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<PlantsAttributes>(
      // Expandimos ambos objetos en el buildQuery
      ...searchByNameQueryObject,
      ...searchByCodeQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;

    return this.http.get<IPaginationResult<PlantsAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(): Observable<IRequestResult<PlantsAttributes[]>> {
    const query = buildQuery<PlantsAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      select(["name", "id", "externalKeys"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<PlantsAttributes[]>>(API_URL);
  }
}
